import { getNodeInstances } from '../endpoints/getNodeInstances';
import { getNodeInstanceSubtree, type NodeInstanceSubtreeChild, type NodeInstanceSubtreeRoot } from '../endpoints/getNodeInstanceSubtree';

// Thin camelCase projections over the shared, validated node-instance loaders,
// for the tree navigator:
//   - getNodeInstance(id)  -> GET /node_instances?node_instance_id=ID (flat,
//     non-recursive), used to walk the parent chain.
//   - getNodeInstanceTreeNode(id)   -> GET /node_instances/:id (recursive), used once on
//     the root to materialise the whole tree in a single call.
// The wire validation and drop/fail policy live in those loaders; here we only
// map to the narrow, deeply-readonly summary shapes the navigator renders.

/** One node instance, the fields the tree navigator renders and navigates by (camelCase). */
export interface NodeInstanceSummary {
  readonly nodeInstanceId: number;
  /** null at the root of a tree. */
  readonly parentNodeInstanceId: number | null;
  readonly nodeDefinitionId: number;
  /** The node definition's name. */
  readonly name: string;
  readonly type: string;
  readonly status: string;
  readonly subStatus: string | null;
  readonly scheduledStartDatetime: string | null;
  readonly scheduledStartTimezone: string | null;
  /** Whether the instance has children, even when they aren't loaded. */
  readonly hasChildNodes: boolean;
}

/** A node instance with its descendants nested under `children`. */
export interface NodeInstanceTreeNode extends NodeInstanceSummary {
  readonly children: readonly NodeInstanceTreeNode[];
}

// The subset of wire fields a summary reads. Both the flat list row and the
// subtree root/child satisfy it; children carry `definition_id` rather than
// `node_definition_id`, so the latter is optional here and defaults to 0 (a
// pre-existing quirk — a later change could map from `definition_id`).
interface Summarizable {
  node_instance_id: number;
  parent_node_instance_id: number | null;
  node_definition_id?: number;
  name: string;
  type: string;
  status: string;
  sub_status: string | null;
  scheduled_start_datetime: string | null;
  scheduled_start_timezone: string | null;
  has_child_nodes: boolean;
}

const toSummary = (wire: Summarizable): NodeInstanceSummary => ({
  nodeInstanceId: wire.node_instance_id,
  parentNodeInstanceId: wire.parent_node_instance_id,
  nodeDefinitionId: wire.node_definition_id ?? 0,
  name: wire.name,
  type: wire.type,
  status: wire.status,
  subStatus: wire.sub_status,
  scheduledStartDatetime: wire.scheduled_start_datetime,
  scheduledStartTimezone: wire.scheduled_start_timezone,
  hasChildNodes: wire.has_child_nodes,
});

const toTreeNode = (node: NodeInstanceSubtreeRoot | NodeInstanceSubtreeChild): NodeInstanceTreeNode => ({
  ...toSummary(node),
  children: node.child_nodes.map(toTreeNode),
});

/**
 * Loads a single node instance by id (non-recursive), for walking the parent
 * chain up to the root. Returns null when the id resolves to no row — e.g. a
 * deleted intermediate instance in a broken parent chain. Throws only if the
 * response envelope is structurally malformed.
 */
export const getNodeInstance = async (id: number): Promise<NodeInstanceSummary | null> => {
  const res = await getNodeInstances(
    {
      node_instance_id: id,
      // The list defaults to parentOnly=true (top-level instances only), which
      // would exclude any child id. We need a node at any depth, so opt out.
      parentOnly: false,
      page: 1,
      limit: 1,
      sortColumn: 'node_instance_id',
      sortOrder: 'asc',
    },
    'getNodeInstance'
  );
  const [row] = res.nodes;
  return row ? toSummary(row) : null;
};

/**
 * Loads a node instance and its full descendant subtree in one call. Used on the
 * root to materialise the whole tree (ancestors of the active node, every
 * sibling at every level, and deeper descendants). Throws if the root is
 * structurally malformed; individual broken descendants are pruned.
 */
export const getNodeInstanceTreeNode = async (id: number): Promise<NodeInstanceTreeNode> => {
  const root = await getNodeInstanceSubtree(id, 'getNodeInstanceTreeNode');
  return toTreeNode(root);
};
